import { useContext, useState } from "react";
import { TransTextInputField } from "../../Atoms/InputFields";
import { ConfirmDialog, useModalState } from "../../Molecules/Dialogs";
import { Icon } from "../../Atoms/Icons";
import Moment from "react-moment";
import {
  patchFormSubmission,
  patchFormSubmissionValues,
} from "../../Service/restapi/formSubmissionService";
import BookingConfirmedConfetti from "../../Atoms/BookingConfirmedConfetti/BookingConfirmedConfetti";
import ContractDrawSign from "./ContractDrawSign";
import { ResourceContext } from "../../Service/Context/ResourceContext";
import { LoadingAnimation } from "../../Service/ResourceAdapter";
import { TransButtonPrimaryWithSpinner } from "../../Atoms/Buttons";
import {
  getContactDescription,
  getContactName,
  isDefined,
} from "../../Utils/utils";
import {
  Group,
  Paper,
  Space,
} from "@mantine/core";
import ProviderSignature from "./ProviderSignature";
import moment from "moment";
import { FormSubmissionS } from "../../Service/FormSubmissionS";
import { ContactS } from "../../Service/restapi/contactService";
import { openCustomerAddressModal } from "../../Modals/CustomerAddressModal";
import { useToasts } from "react-toast-notifications";
import Agreements from "./Agreements";
import { useLingui } from "@lingui/react";
import {
  getDocumentLanguageTranslationKey,
  getDocumentTranslator,
} from "../Document/documentLanguage";

const ContractSign = ({
  contract,
  formSubmission,
  values,
  resource,
  customerSignType,
  handleChange,
  patchSubmissionValue,
  reload,
}) => {
  const { i18n } = useLingui();
  const t = getDocumentTranslator(i18n, contract.language);
  const tKey = (key) =>
    getDocumentLanguageTranslationKey(contract.language, key);
  const { customerSignedAt, customerPartnerSignedAt } = formSubmission;
  const { show, toggle } = useModalState(false);
  const { addToast } = useToasts();
  const [loading, setLoading] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  const [confettiShoot, setConfettiShoot] = useState(false);
  const {
    loading: _loading,
    customerContact,
    partnerIncluded,
    setCustomerContact,
  } = useContext(ResourceContext);

  const { signImage, partnerSignImage, sign, partnerSign } = values;

  if (_loading) {
    return <LoadingAnimation />;
  }

  const collectSignatures = () => {
    if (customerSignType === "TEXT_FIELD") {
      return {
        sign,
        partnerSign: partnerIncluded ? partnerSign : undefined,
      };
    }
    return {
      signImage,
      partnerSignImage: partnerIncluded ? partnerSignImage : undefined,
    };
  };

  const signed = async () => {
    setLoading(true);
    await patchFormSubmissionValues(formSubmission.id, collectSignatures());
    patchFormSubmission(formSubmission.id, { submitted: true })
      .then((resp) => {
        if (resp.ok) {
          setConfettiShoot(true);
          reload();
        } else {
          addToast(
            t("contractSigningError"),
            { appearance: "error" }
          );
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  let styles = {
    container: {
      marginTop: 20,
      marginBottom: 10,
      display: "flex",
      flexWrap: "wrap",
      flexDirection: "column",
    },
    sign: {
      fontFamily: "SafiarSignature, Courier",
      // paddingTop: 0,
      fontSize: 32,
      height: 50,
      maxWidth: 250,
    },
  };

  const verifyAgreements = () => {
    const requiredAgreementsIds = contract.agreements
      .filter((a) => a.required)
      .map((a) => a.id);
    for (let i = 0; i < requiredAgreementsIds.length; i++) {
      if (!FormSubmissionS.isValuePresent(requiredAgreementsIds[i], values)) {
        return false;
      }
    }
    return true;
  };

  const signatureCompleted = () => {
    if (customerSignType === "TEXT_FIELD") {
      return partnerIncluded ? sign && partnerSign : sign;
    }
    return partnerIncluded
      ? isDefined(signImage) && isDefined(partnerSignImage)
      : isDefined(signImage);
  };

  const onSignClick = () => {
    setShowErrors(false);
    if (!verifyAgreements()) {
      setShowErrors(true);
      return;
    }
    if (!ContactS.isAddressAndPartnerDefined(customerContact)) {
      openCustomerAddressModal({
        documentType: "CONTRACT",
        language: contract.language,
        contact: customerContact,
        onFinish: (c) => {
          setCustomerContact(c);
          signed();
        },
        profileId: resource.profileId,
      });
    } else {
      toggle();
    }
  };

  return (
    <>
      <Agreements
        submitted={formSubmission.lastSubmitted !== null}
        agreements={contract.agreements}
        values={values}
        patchSubmissionValue={patchSubmissionValue}
        showErrors={showErrors}
        language={contract.language}
      />
      <hr />
      <div>
        {t("contractAcceptanceText")}
      </div>

      <div style={styles.container}>
        {customerSignType && customerSignType === "TEXT_FIELD" && (
          <div>
            <Group position="center">
              <TransTextInputField
                disabled={formSubmission.lastSubmitted !== null}
                style={{ marginRight: 10 }}
                inputStyles={styles.sign}
                onChange={(e) => {
                  handleChange("sign", e.target.value);
                }}
                name="sign"
                value={sign}
                placeholder={getContactDescription(customerContact)}
                onKeyPress={(event) => {
                  if (/[0-9,\.]/.test(event.key)) {
                    event.preventDefault();
                  }
                }}
              />
              {formSubmission.lastSubmitted && (
                <p>{`${t("contractSignedAt")}: ${moment(customerSignedAt).format(
                  "L"
                )}`}</p>
              )}
            </Group>
            {partnerIncluded && (
              <>
                <Group position="center">
                  <TransTextInputField
                    disabled={formSubmission.lastSubmitted !== null}
                    style={{ marginRight: 10 }}
                    inputStyles={styles.sign}
                    onChange={(e) => {
                      handleChange("partnerSign", e.target.value);
                    }}
                    name="partnerSign"
                    value={partnerSign ? partnerSign : ""}
                    placeholder={getContactName(customerContact.partner)}
                    onKeyPress={(event) => {
                      if (/[0-9,\.]/.test(event.key)) {
                        event.preventDefault();
                      }
                    }}
                  />
                  {formSubmission.lastSubmitted && (
                    <p>{`${t("contractSignedAt")}: ${moment(
                      customerPartnerSignedAt
                    ).format("L")}`}</p>
                  )}
                </Group>
                <hr style={{ marginBottom: 40 }} />
              </>
            )}
          </div>
        )}

        {customerSignType && customerSignType === "BASE64" && (
          <>
            <h5 style={{ marginBottom: 0 }}>
              {getContactDescription(customerContact)}
            </h5>

            {!formSubmission.lastSubmitted && (
              <ContractDrawSign
                language={contract.language}
                onChange={(base64) => handleChange("signImage", base64)}
              />
            )}
            {formSubmission.lastSubmitted && (
              <>
                <div
                  style={{ marginLeft: 20, alignSelf: "center", height: 50 }}
                >
                  <Icon value={"BOOKED"} style={{ color: "green" }} />{" "}
                  {t("contractSignedAt")}{" "}
                  <Moment format="L">{customerSignedAt}</Moment>
                </div>
                <Paper shadow="xl" mb="xl">
                  <img
                    alt="customerSign"
                    style={{ width: "100%" }}
                    src={signImage}
                  />
                </Paper>
              </>
            )}

            {partnerIncluded && customerContact.partner && (
              <>
                <h5 style={{ marginBottom: 0, marginTop: 10 }}>
                  {getContactName(customerContact.partner)}
                </h5>

                {!formSubmission.lastSubmitted && (
                  <ContractDrawSign
                    language={contract.language}
                    onChange={(base64) =>
                      handleChange("partnerSignImage", base64)
                    }
                  />
                )}
                {formSubmission.lastSubmitted && (
                  <>
                    <div
                      style={{
                        marginLeft: 20,
                        alignSelf: "center",
                        height: 50,
                      }}
                    >
                      <Icon value={"BOOKED"} style={{ color: "green" }} />{" "}
                      {t("contractSignedAt")}{" "}
                      <Moment format="L">{customerSignedAt}</Moment>
                    </div>
                    <Paper shadow="xl" mb="xl">
                      <img
                        alt="customerSign"
                        style={{ width: "100%" }}
                        src={partnerSignImage}
                      />
                    </Paper>
                  </>
                )}
              </>
            )}
          </>
        )}

        {!formSubmission.lastSubmitted && (
          <>
            <TransButtonPrimaryWithSpinner
              isLoading={loading}
              disabled={!signatureCompleted() || loading}
              style={{
                height: 40,
                width: 300,
                minWidth: 200,
                maxWidth: 300,
                alignSelf: "center",
                marginTop: 25,
              }}
              onClickHandler={onSignClick}
              text={tKey("contractSubmitButton")}
            />
            {!signatureCompleted() && (
              <p
                style={{ marginTop: 10, color: "gray", fontSize: 12 }}
                align="center"
              >
                {t("contractSignatureRequired")}
              </p>
            )}
          </>
        )}
        <BookingConfirmedConfetti active={confettiShoot} />
        <ConfirmDialog
          text={tKey("contractConfirmText")}
          subject={tKey("contractConfirmSubject")}
          confirmText={tKey("contractSubmitButton")}
          cancelText={tKey("cancelButton")}
          open={show}
          toggle={toggle}
          handler={signed}
        />
      </div>
      <Space h="xl" />
      <ProviderSignature
        signedAt={resource.lastModified}
        language={contract.language}
      />
    </>
  );
};

export default ContractSign;
